import {getColorIndicator} from "../../../../utils/color-indicator/color-indicator.utils";
import {DispatchRulesDataState} from "../../../../providers/DispatchRulesDataProvider";
import {StopColor} from "../../common/components/cards/common/components/ManifestCardCommonV2";
import {TManifestEnhanced} from "../../../common/types";
import {AssignmentCandidate, Manifest} from "../../../../generated/graphql";
import {metersToMiles} from "../../../../utils/General";

// DP-3292. A rule with a Manifest Attribute and no comparison type is tagged
// `conditions.name = "ManifestOnly"` by RuleGenerator.service.ts: a *manifest* colour rule,
// which colours the manifest header and never a stop.
//
// Two properties here are load-bearing and must survive any refactor:
//
//   1. the fact bundle holds `manifest` and NO `stop`, so no stop-scoped condition can match;
//   2. the result is pushed WITHOUT a `jobStopID`, so it can never be matched to a row —
//      ManifestDriverDetailedCard looks rows up by `jobStopID` — and reaches only
//      getHighestRankedColor, i.e. the header.
//
// Extracted from getManifestColors so the flag-on header paths in ManifestDriverList and
// ManifestDetailsV2 share one definition with the legacy flag-off path instead of growing a
// second copy that can drift.
export const getManifestOnlyColor = async ({
  manifest,
  ruleContextState
}: {
  manifest: TManifestEnhanced;
  ruleContextState: DispatchRulesDataState;
}): Promise<StopColor | undefined> => {
  const engine = ruleContextState.getManifestOnlyEngine(manifest);
  if (!engine) return undefined;

  const color = await getColorIndicator(engine);
  return color ? ({color: color} as StopColor) : undefined;
};

export type SuggestionDelta = {text: string; isNegative: boolean};

const KEEP_IN_BEST_ORDER =
  "Optimizing manifests and keeping them in the best order saves drivers time, makes ETAs more accurate, and helps suggested assignment work better.";

// DP-3267 D15: shown when hovering a chip whose displayed value is negative
export const SUGGESTION_NEGATIVE_TOOLTIP = {
  title: "Why is this negative?",
  time: [
    "This manifest’s existing stops aren’t in the best order. If you add this job and optimize the stops, the driver would finish sooner than they will now.",
    KEEP_IN_BEST_ORDER
  ],
  distance: [
    "This manifest’s existing stops aren’t in the best order. If you add this job and optimize the stops, the driver would drive fewer miles than they will now.",
    KEEP_IN_BEST_ORDER
  ]
};

// The numbers the chips show, with -0 normalised to 0 (D13). The ranking uses the same values (D24).
const chipMinutes = (seconds: number) => Math.round(seconds / 60) || 0;
const chipMiles = (optimizedMeters: number, baselineMeters: number) =>
  metersToMiles(optimizedMeters - baselineMeters) || 0;

// Whole minutes with no "+" sign (D12). "Negative" follows the rounded value (D13).
export const formatAdditionalTime = (seconds: number | null | undefined): SuggestionDelta | null => {
  if (seconds == null) return null;
  const minutes = chipMinutes(seconds);
  const hours = Math.floor(Math.abs(minutes) / 60);
  const text = `${minutes < 0 ? "-" : ""}${hours > 0 ? `${hours}h ` : ""}${Math.abs(minutes) % 60}m`;
  return {text, isNegative: minutes < 0};
};

// There is no added-distance field, so it is the difference of the two route distances (D11), rounded as in D13.
export const formatAdditionalDistance = (
  optimizedMeters: number | null | undefined,
  baselineMeters: number | null | undefined
): SuggestionDelta | null => {
  if (optimizedMeters == null || baselineMeters == null) return null;
  const miles = chipMiles(optimizedMeters, baselineMeters);
  return {text: `${miles} miles`, isNegative: miles < 0};
};

// Ascending, with unknown values after every known one
const compareKnownFirst = (a: number | null, b: number | null) =>
  a == null || b == null ? Number(a == null) - Number(b == null) : a - b;

const minutesOf = ({additionalTime}: AssignmentCandidate) =>
  additionalTime == null ? null : chipMinutes(additionalTime);
const milesOf = ({optimizedDistance, baselineDistance}: AssignmentCandidate) =>
  optimizedDistance == null || baselineDistance == null ? null : chipMiles(optimizedDistance, baselineDistance);

const byChipValues = (a: AssignmentCandidate, b: AssignmentCandidate) =>
  compareKnownFirst(minutesOf(a), minutesOf(b)) || compareKnownFirst(milesOf(a), milesOf(b));

/**
 * DP-3267 D16, D24: while a suggestion is active, the cards run from the smallest added time to the largest, as the
 * chips show it (whole minutes); equal minutes go by the fewer added miles the chip shows. Unknown values go after the
 * known ones. Remaining ties keep the candidates' (backend) order, which already puts the shorter optimized route
 * first. A manifest with no candidate goes last. Returns a new array; neither input is mutated.
 */
export const sortManifestsBySuggestion = (manifests: Manifest[], candidates: AssignmentCandidate[]): Manifest[] => {
  const rank = new Map(
    [...candidates].sort(byChipValues).map((candidate, index) => [candidate.manifestDriverId, index])
  );
  const rankOf = (manifest: Manifest) => rank.get(manifest.manifestDriverId) ?? candidates.length;
  return [...manifests].sort((a, b) => rankOf(a) - rankOf(b));
};
